import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Slider } from "../components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  CreditCard, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  RefreshCw, 
  DollarSign,
  Sparkles,
  Clock,
  Wallet,
  ArrowRight,
  Shield,
  Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreditPage() {
  const { user } = useAuth();
  const [eligibility, setEligibility] = useState(null);
  const [advances, setAdvances] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advanceLoading, setAdvanceLoading] = useState(false);
  const [repayLoading, setRepayLoading] = useState(null);
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [repayDialogOpen, setRepayDialogOpen] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [advanceAmount, setAdvanceAmount] = useState(25);

  const fetchData = async () => {
    try {
      const [eligibilityRes, advancesRes, walletRes] = await Promise.all([
        axios.get(`${API}/credit/eligibility`, { withCredentials: true }),
        axios.get(`${API}/credit/advances`, { withCredentials: true }),
        axios.get(`${API}/wallet`, { withCredentials: true }),
      ]);
      setEligibility(eligibilityRes.data);
      setAdvances(advancesRes.data);
      setWallet(walletRes.data);
    } catch (error) {
      console.error("Failed to fetch credit data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRequestAdvance = async () => {
    if (advanceAmount <= 0) {
      toast.error("Please select a valid amount");
      return;
    }

    if (advanceAmount > eligibility.max_amount) {
      toast.error(`Maximum amount is $${eligibility.max_amount}`);
      return;
    }

    setAdvanceLoading(true);
    try {
      await axios.post(
        `${API}/credit/advance`,
        {
          amount: advanceAmount,
          currency: "USD",
        },
        { withCredentials: true }
      );
      toast.success("🎉 Advance approved! Funds added to your wallet.");
      setAdvanceDialogOpen(false);
      setAdvanceAmount(25);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get advance");
    } finally {
      setAdvanceLoading(false);
    }
  };

  const openRepayDialog = (advance) => {
    setSelectedAdvance(advance);
    setRepayDialogOpen(true);
  };

  const handleRepay = async () => {
    if (!selectedAdvance) return;
    
    setRepayLoading(selectedAdvance.advance_id);
    try {
      await axios.post(`${API}/credit/repay/${selectedAdvance.advance_id}`, {}, { withCredentials: true });
      toast.success("✅ Advance repaid successfully! Your credit score improved.");
      setRepayDialogOpen(false);
      setSelectedAdvance(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to repay. Check your wallet balance.");
    } finally {
      setRepayLoading(null);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 700) return "text-green-600";
    if (score >= 500) return "text-[#14B8A6]";
    if (score >= 400) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreLabel = (score) => {
    if (score >= 800) return "Excellent";
    if (score >= 650) return "Very Good";
    if (score >= 500) return "Good";
    if (score >= 400) return "Fair";
    return "Building";
  };

  const getScoreBgColor = (score) => {
    if (score >= 700) return "bg-green-500";
    if (score >= 500) return "bg-[#14B8A6]";
    if (score >= 400) return "bg-amber-500";
    return "bg-red-500";
  };

  const calculateFee = (amount) => {
    return amount * (eligibility?.fee_percentage || 5) / 100;
  };

  const getActiveAdvance = () => advances.find(a => a.status === "active");
  const activeAdvance = getActiveAdvance();

  const getWalletBalance = () => {
    return wallet?.balances?.find(b => b.currency === "USD")?.amount || 0;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#14B8A6] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="credit-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {user?.account_type === "sme" ? "Working Capital" : "Liquidity Advances"}
          </h1>
          <p className="text-slate-600 mt-1">
            Get quick access to funds when you need them
          </p>
        </div>

        {/* Credit Score Hero */}
        <Card className="overflow-hidden border-0 shadow-xl" data-testid="credit-score-hero">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Score Display */}
              <div className="text-center md:text-left">
                <p className="text-slate-400 mb-2">Your Credit Score</p>
                <div className="flex items-baseline gap-2 justify-center md:justify-start">
                  <span className={`text-6xl font-bold ${getScoreColor(eligibility?.credit_score || 0)}`}>
                    {eligibility?.credit_score || 0}
                  </span>
                  <span className="text-slate-400">/1000</span>
                </div>
                <Badge className={`mt-3 ${getScoreBgColor(eligibility?.credit_score || 0)}`}>
                  <Sparkles className="h-3 w-3 mr-1" />
                  {getScoreLabel(eligibility?.credit_score || 0)}
                </Badge>
                
                <div className="mt-6">
                  <Progress 
                    value={(eligibility?.credit_score || 0) / 10} 
                    className="h-3 bg-slate-700" 
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-2">
                    <span>0</span>
                    <span>400</span>
                    <span>600</span>
                    <span>800</span>
                    <span>1000</span>
                  </div>
                </div>
              </div>

              {/* Eligibility Info */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  {eligibility?.eligible ? (
                    <>
                      <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">You&apos;re Eligible!</p>
                        <p className="text-sm text-slate-300">Instant approval available</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="h-10 w-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <AlertCircle className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">Not Yet Eligible</p>
                        <p className="text-sm text-slate-300">{eligibility?.reason}</p>
                      </div>
                    </>
                  )}
                </div>

                {eligibility?.eligible && (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-300">Max Available</span>
                      <span className="font-bold text-white">${eligibility?.max_amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Fee</span>
                      <span className="text-white">{eligibility?.fee_percentage}% flat</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Repayment</span>
                      <span className="text-white">{eligibility?.repayment_days} days</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Active Advance Alert */}
        {activeAdvance && (
          <Card className="border-2 border-amber-300 bg-amber-50" data-testid="active-advance-alert">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Active Advance</h3>
                    <p className="text-sm text-slate-600">
                      Repay ${activeAdvance.total_repayment} by {new Date(activeAdvance.due_date).toLocaleDateString()}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-slate-500">Amount: <strong className="text-slate-900">${activeAdvance.amount}</strong></span>
                      <span className="text-slate-500">Fee: <strong className="text-slate-900">${activeAdvance.fee}</strong></span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-slate-500">
                    Wallet Balance: <strong className="text-slate-900">${getWalletBalance().toFixed(2)}</strong>
                  </p>
                  <Button
                    className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                    onClick={() => openRepayDialog(activeAdvance)}
                    disabled={getWalletBalance() < activeAdvance.total_repayment}
                    data-testid="repay-active-btn"
                  >
                    <DollarSign className="h-4 w-4 mr-2" />
                    Repay ${activeAdvance.total_repayment}
                  </Button>
                  {getWalletBalance() < activeAdvance.total_repayment && (
                    <p className="text-xs text-red-600">Insufficient balance. Add funds first.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Get Advance CTA */}
        {eligibility?.eligible && !activeAdvance && (
          <Card className="border-2 border-[#14B8A6] bg-[#14B8A6]/5" data-testid="get-advance-cta">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-2xl bg-[#14B8A6] flex items-center justify-center">
                    <Zap className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Get Instant Funds</h3>
                    <p className="text-slate-600 mt-1">
                      Borrow up to <strong>${eligibility.max_amount}</strong> instantly. 
                      Repay within {eligibility.repayment_days} days with just {eligibility.fee_percentage}% fee.
                    </p>
                    <div className="flex items-center gap-4 mt-3">
                      <Badge variant="secondary" className="bg-white">
                        <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                        No collateral
                      </Badge>
                      <Badge variant="secondary" className="bg-white">
                        <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                        Instant approval
                      </Badge>
                      <Badge variant="secondary" className="bg-white">
                        <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                        No hidden fees
                      </Badge>
                    </div>
                  </div>
                </div>
                <Button
                  size="lg"
                  className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488] h-14 px-8 text-lg"
                  onClick={() => setAdvanceDialogOpen(true)}
                  data-testid="get-advance-btn"
                >
                  Get Advance
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* How It Works */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: 1, icon: TrendingUp, title: "Build Score", desc: "Use OnePay regularly to improve your credit score" },
                { step: 2, icon: DollarSign, title: "Request", desc: "Choose amount up to your limit" },
                { step: 3, icon: Wallet, title: "Receive", desc: "Funds added to wallet instantly" },
                { step: 4, icon: CheckCircle, title: "Repay", desc: "Pay back within 14 days" },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="h-12 w-12 rounded-full bg-[#14B8A6] text-white flex items-center justify-center mx-auto mb-3">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h4 className="font-medium text-slate-900">{item.title}</h4>
                  <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Advance History */}
        <Card className="border-slate-200" data-testid="advances-list">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Advance History</CardTitle>
          </CardHeader>
          <CardContent>
            {advances.length > 0 ? (
              <div className="space-y-4">
                {advances.map((adv) => (
                  <div
                    key={adv.advance_id}
                    className={`flex items-center justify-between p-4 rounded-xl ${
                      adv.status === "active" ? "bg-amber-50 border border-amber-200" : "bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        adv.status === "active" ? "bg-amber-100" : "bg-green-100"
                      }`}>
                        <CreditCard className={`h-5 w-5 ${
                          adv.status === "active" ? "text-amber-600" : "text-green-600"
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          ${adv.amount} Advance
                        </p>
                        <p className="text-xs text-slate-500">
                          {adv.status === "active" 
                            ? `Due ${new Date(adv.due_date).toLocaleDateString()}`
                            : `Repaid ${adv.repaid_at ? new Date(adv.repaid_at).toLocaleDateString() : ""}`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-900">
                          Total: ${adv.total_repayment}
                        </p>
                        <p className="text-xs text-slate-500">
                          Fee: ${adv.fee}
                        </p>
                      </div>
                      {adv.status === "active" ? (
                        <Button
                          size="sm"
                          className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                          onClick={() => openRepayDialog(adv)}
                          disabled={repayLoading === adv.advance_id}
                          data-testid={`repay-${adv.advance_id}`}
                        >
                          {repayLoading === adv.advance_id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            "Repay Now"
                          )}
                        </Button>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Repaid
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <CreditCard className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No advances yet</p>
                <p className="text-sm mt-1">Get your first advance to build credit history</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Request Advance Dialog */}
        <Dialog open={advanceDialogOpen} onOpenChange={setAdvanceDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-[#14B8A6]" />
                Request Advance
              </DialogTitle>
              <DialogDescription>
                Choose how much you need. Funds will be added to your wallet instantly.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 pt-4">
              {/* Amount Selector */}
              <div className="space-y-4">
                <div className="text-center">
                  <span className="text-4xl font-bold text-slate-900">${advanceAmount}</span>
                </div>
                <Slider
                  value={[advanceAmount]}
                  onValueChange={(v) => setAdvanceAmount(v[0])}
                  max={eligibility?.max_amount || 50}
                  min={10}
                  step={5}
                  className="py-4"
                />
                <div className="flex justify-between text-sm text-slate-500">
                  <span>$10</span>
                  <span>${eligibility?.max_amount || 50}</span>
                </div>
              </div>

              {/* Breakdown */}
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Advance Amount</span>
                  <span className="font-medium">${advanceAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Fee ({eligibility?.fee_percentage}%)</span>
                  <span className="font-medium">${calculateFee(advanceAmount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold pt-2 border-t border-slate-200">
                  <span>Total Repayment</span>
                  <span className="text-[#14B8A6]">${(advanceAmount + calculateFee(advanceAmount)).toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500 pt-2">
                  <Clock className="h-4 w-4" />
                  <span>Due in {eligibility?.repayment_days} days</span>
                </div>
              </div>

              <Button
                className="w-full h-12 rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                onClick={handleRequestAdvance}
                disabled={advanceLoading}
                data-testid="confirm-advance-btn"
              >
                {advanceLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Zap className="h-4 w-4 mr-2" />
                )}
                Get ${advanceAmount} Now
              </Button>

              <p className="text-xs text-center text-slate-500">
                By proceeding, you agree to repay ${(advanceAmount + calculateFee(advanceAmount)).toFixed(2)} within {eligibility?.repayment_days} days
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Repay Confirmation Dialog */}
        <AlertDialog open={repayDialogOpen} onOpenChange={setRepayDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-[#14B8A6]" />
                Confirm Repayment
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p>You are about to repay your advance. This will deduct funds from your wallet.</p>
                  
                  {selectedAdvance && (
                    <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Repayment Amount</span>
                        <span className="font-bold text-slate-900">${selectedAdvance.total_repayment}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Your Wallet Balance</span>
                        <span className={`font-medium ${getWalletBalance() >= selectedAdvance.total_repayment ? "text-green-600" : "text-red-600"}`}>
                          ${getWalletBalance().toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="text-slate-500">After Repayment</span>
                        <span className="font-medium">
                          ${Math.max(0, getWalletBalance() - selectedAdvance.total_repayment).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRepay}
                className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                disabled={repayLoading || (selectedAdvance && getWalletBalance() < selectedAdvance.total_repayment)}
              >
                {repayLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Confirm Repayment
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
